import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './contexts/ToastContext';
import { ToastContainer } from './components/ToastContainer';
import { useAuthStore } from './store/authStore';

// Components
import { ProtectedRoute } from './components/ProtectedRoute';
import { DashboardLayout } from './components/DashboardLayout';
import { FeatureRoute } from './components/FeatureRoute';

// Auth Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';

// Dashboard Pages
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Accounting from './pages/Accounting';
import ManualCommissionReporting from './pages/accounting/ManualCommissionReporting';
import Partners from './pages/accounting/Partners';
import AdminUsers from './pages/AdminUsers';
import Divisions from './pages/admin/Divisions';
import Products from './pages/crm/Products';

// CRM Pages (Stream O)
import Operators from './pages/crm/Operators';
import OperatorDetail from './pages/crm/OperatorDetail';
import Contacts from './pages/crm/Contacts';

// CRM Pages (Stream A)
import Opportunities from './pages/crm/Opportunities';
import OpportunityDetail from './pages/crm/OpportunityDetail';
import Activities from './pages/crm/Activities';

// CRM Pages (Phase 2 Stream L — Sales Logs)
import SalesLogs from './pages/crm/SalesLogs';
import SalesLogDetail from './pages/crm/SalesLogDetail';

// CRM Pages (Phase 2 Stream P — Planning)
import OperatorCallTargets from './pages/crm/OperatorCallTargets';
import DistributorInitiatives from './pages/crm/DistributorInitiatives';

// CRM Pages (Phase 2 Stream T — Sales Planning)
import SalesTargets from './pages/crm/SalesTargets';
import ClientManagerTargets from './pages/crm/ClientManagerTargets';
import Goals from './pages/crm/Goals';

// Field Visit Requests (P-EXE-88)
import FieldVisitRequestForm from './pages/field-visits/FieldVisitRequestForm';

// Dev-only pattern library catalog. The DEV check is replaced at build time,
// so production bundles drop the import entirely.
const Patterns = import.meta.env.DEV ? lazy(() => import('./pages/Patterns')) : null;

function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const initialize = useAuthStore((state) => state.initialize);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);

  useEffect(() => {
    initialize().then(() => setIsInitialized(true));
  }, [initialize]);

  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/accounting" element={<Accounting />} />
              <Route
                path="/accounting/manual-commission-reporting"
                element={<ManualCommissionReporting />}
              />
              <Route path="/accounting/partners" element={<Partners />} />

              {/* CRM routes, feature-flag gated */}
              <Route element={<FeatureRoute flag="crm" />}>
                <Route path="/crm/operators" element={<Operators />} />
                <Route path="/crm/operators/:id" element={<OperatorDetail />} />
                <Route path="/crm/contacts" element={<Contacts />} />
                <Route path="/crm/products" element={<Products />} />
                <Route path="/crm/opportunities" element={<Opportunities />} />
                <Route path="/crm/opportunities/:id" element={<OpportunityDetail />} />
                <Route path="/crm/activities" element={<Activities />} />
                <Route path="/crm/sales-logs" element={<SalesLogs />} />
                <Route path="/crm/sales-logs/:id" element={<SalesLogDetail />} />

                {/* Planning routes — manager-only (mirrors Rails CrmManagerGate) */}
                <Route element={<ProtectedRoute requireCrmManager />}>
                  <Route path="/crm/call-targets" element={<OperatorCallTargets />} />
                  <Route path="/crm/distributor-initiatives" element={<DistributorInitiatives />} />
                  <Route path="/crm/sales-targets" element={<SalesTargets />} />
                  <Route path="/crm/client-manager-targets" element={<ClientManagerTargets />} />
                  <Route path="/crm/goals" element={<Goals />} />
                </Route>
              </Route>

              {/* Field Visit Requests, feature-flag gated */}
              <Route element={<FeatureRoute flag="field_visit_requests" />}>
                <Route path="/field-visits/new" element={<FieldVisitRequestForm />} />
              </Route>

              {/* Admin only routes */}
              <Route element={<ProtectedRoute requireAdmin />}>
                <Route path="/admin/users" element={<AdminUsers />} />
                {/* Divisions are a CRM concept; the API 404s them while the flag is off */}
                <Route element={<FeatureRoute flag="crm" />}>
                  <Route path="/admin/divisions" element={<Divisions />} />
                </Route>
              </Route>
            </Route>
          </Route>

          {/* Redirect root to dashboard or login */}
          <Route
            path="/"
            element={
              <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />
            }
          />

          {/* Dev-only: no auth, so the shell and components can be screenshot */}
          {Patterns && (
            <Route element={<DashboardLayout />}>
              <Route
                path="/patterns"
                element={
                  <Suspense fallback={null}>
                    <Patterns />
                  </Suspense>
                }
              />
            </Route>
          )}

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>

      <ToastContainer />
    </ToastProvider>
  );
}

export default App;
